import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, CalendarOff, Save, X } from "lucide-react";
import { cn } from "@/lib/utils";

const DAY_NAMES = ["Sö", "Må", "Ti", "On", "To", "Fr", "Lö"];

interface RecurringBlockDetailDialogProps {
  block: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDate: Date;
  courts: { id: string; name: string }[];
  onUpdated: () => void;
  onHighlightCourts?: (courtIds: string[]) => void;
}

export function RecurringBlockDetailDialog({
  block,
  open,
  onOpenChange,
  selectedDate,
  courts,
  onUpdated,
  onHighlightCourts,
}: RecurringBlockDetailDialogProps) {
  const { t } = useTranslation("admin");
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state — all fields
  const [editName, setEditName] = useState(block.name ?? "");
  const [editCategory, setEditCategory] = useState(block.block_category ?? "revenue");
  const [editCourtIds, setEditCourtIds] = useState<string[]>(block.court_ids ?? []);
  const [editValidFrom, setEditValidFrom] = useState(block.valid_from ?? "");
  const [editValidUntil, setEditValidUntil] = useState(block.valid_until ?? "");
  const [editDayOfWeek, setEditDayOfWeek] = useState<number>(block.day_of_week ?? 0);
  const [editInterval, setEditInterval] = useState(String(block.interval_weeks ?? 1));
  const [editPrice, setEditPrice] = useState<string>(String(block.price ?? 0));
  const [editEstimatedValue, setEditEstimatedValue] = useState<string>(String(block.estimated_value ?? ""));
  const [editConfirmedRevenue, setEditConfirmedRevenue] = useState(block.is_confirmed_revenue ?? false);
  const [editComment, setEditComment] = useState(block.comment ?? "");
  const [editColor, setEditColor] = useState(block.color ?? "#64748B");

  // Sync court highlighting while editing
  useEffect(() => {
    if (editing && onHighlightCourts) {
      onHighlightCourts(editCourtIds);
    }
    return () => { if (onHighlightCourts) onHighlightCourts([]); };
  }, [editing, editCourtIds, onHighlightCourts]);

  const startTime = block.start_time?.slice(0, 5) ?? "";
  const endTime = block.end_time?.slice(0, 5) ?? "";
  const courtNames = (block.court_ids ?? [])
    .map((id: string) => courts.find((c) => c.id === id)?.name ?? id)
    .join(", ");
  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const isRevenue = editCategory === "revenue";

  const resetEditState = () => {
    setEditName(block.name ?? "");
    setEditCategory(block.block_category ?? "revenue");
    setEditCourtIds(block.court_ids ?? []);
    setEditValidFrom(block.valid_from ?? "");
    setEditValidUntil(block.valid_until ?? "");
    setEditDayOfWeek(block.day_of_week ?? 0);
    setEditInterval(String(block.interval_weeks ?? 1));
    setEditPrice(String(block.price ?? 0));
    setEditEstimatedValue(String(block.estimated_value ?? ""));
    setEditConfirmedRevenue(block.is_confirmed_revenue ?? false);
    setEditComment(block.comment ?? "");
    setEditColor(block.color ?? "#64748B");
  };

  const handleSave = async () => {
    if (!editName.trim()) {
      toast.error(t("recurringBlock.nameRequired"));
      return;
    }
    if (editCourtIds.length === 0) {
      toast.error(t("recurringBlock.courtRequired"));
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("recurring_blocks" as any)
      .update({
        name: editName.trim(),
        block_category: editCategory,
        court_ids: editCourtIds,
        valid_from: editValidFrom || null,
        valid_until: editValidUntil || null,
        day_of_week: editDayOfWeek,
        interval_weeks: parseInt(editInterval) || 1,
        price: parseFloat(editPrice) || 0,
        estimated_value: editEstimatedValue ? parseFloat(editEstimatedValue) : null,
        is_confirmed_revenue: editConfirmedRevenue,
        comment: editComment || null,
        color: editColor || null,
      })
      .eq("id", block.id);
    setSaving(false);
    if (error) {
      toast.error(t("recurringBlock.saveError") + ": " + (error.message ?? String(error)));
    } else {
      toast.success(t("recurringBlock.updated"));
      setEditing(false);
      onUpdated();
    }
  };

  const handleDelete = async () => {
    const { error } = await supabase
      .from("recurring_blocks" as any)
      .update({ status: "deleted" })
      .eq("id", block.id);
    if (error) {
      toast.error(t("recurringBlock.deleteError") + ": " + (error.message ?? String(error)));
    } else {
      toast.success(t("recurringBlock.deleted"));
      onOpenChange(false);
      onUpdated();
    }
  };

  const handleSkipDate = async () => {
    const currentSkipped: string[] = block.skipped_dates ?? [];
    if (currentSkipped.includes(dateStr)) {
      toast.info(t("recurringBlock.alreadySkipped"));
      return;
    }
    const { error } = await supabase
      .from("recurring_blocks" as any)
      .update({ skipped_dates: [...currentSkipped, dateStr] })
      .eq("id", block.id);
    if (error) {
      toast.error(t("recurringBlock.skipError") + ": " + (error.message ?? String(error)));
    } else {
      toast.success(t("recurringBlock.skipped", { date: format(selectedDate, "d MMMM", { locale: sv }) }));
      onOpenChange(false);
      onUpdated();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: block.color || "#64748B" }}
              />
              {editing ? t("recurringBlock.editTitle") : (block.name || t("recurringBlock.title"))}
            </DialogTitle>
            <DialogDescription>
              {startTime}–{endTime} · {courtNames}
            </DialogDescription>
          </DialogHeader>

          {editing ? (
            <div className="space-y-3 pt-2">
              {/* Name */}
              <div>
                <Label className="text-xs">{t("recurringBlock.name")} *</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-9 text-sm" />
              </div>

              {/* Block category */}
              <div>
                <Label className="text-xs">{t("recurringBlock.blockType")}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" onClick={() => setEditCategory("revenue")}
                    className={cn("flex items-center gap-2 p-2 rounded-lg border-2 text-xs transition-colors",
                      editCategory === "revenue" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
                    Intäktsgenererande
                  </button>
                  <button type="button" onClick={() => setEditCategory("operational")}
                    className={cn("flex items-center gap-2 p-2 rounded-lg border-2 text-xs transition-colors",
                      editCategory === "operational" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
                    Drift
                  </button>
                </div>
              </div>

              {/* Courts */}
              <div>
                <Label className="text-xs">{t("recurringBlock.courts")} *</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1">
                  {courts.map((c) => (
                    <label key={c.id} className="flex items-center gap-1.5 text-xs">
                      <Checkbox
                        checked={editCourtIds.includes(c.id)}
                        onCheckedChange={(checked) => {
                          setEditCourtIds(checked
                            ? [...editCourtIds, c.id]
                            : editCourtIds.filter((id) => id !== c.id));
                        }}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("recurringBlock.startDate")}</Label>
                  <Input type="date" value={editValidFrom} onChange={(e) => setEditValidFrom(e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">{t("recurringBlock.endDate")}</Label>
                  <Input type="date" value={editValidUntil} onChange={(e) => setEditValidUntil(e.target.value)} className="h-9 text-sm" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">{t("recurringBlock.endDateHint")}</p>
                </div>
              </div>

              {/* Day of week */}
              <div>
                <Label className="text-xs">{t("recurringBlock.weekday")}</Label>
                <div className="flex gap-1.5 mt-1">
                  {DAY_NAMES.map((name, i) => (
                    <button key={i} type="button" onClick={() => setEditDayOfWeek(i)}
                      className={cn("w-8 h-8 rounded-md text-xs font-medium border transition-colors",
                        editDayOfWeek === i ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:bg-accent")}>
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Interval */}
              <div>
                <Label className="text-xs">{t("recurringBlock.repeat")}</Label>
                <Select value={editInterval} onValueChange={setEditInterval}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t("recurringBlock.everyWeek")}</SelectItem>
                    <SelectItem value="2">{t("recurringBlock.everyOtherWeek")}</SelectItem>
                    <SelectItem value="3">{t("recurringBlock.everyNWeeks", { n: 3 })}</SelectItem>
                    <SelectItem value="4">{t("recurringBlock.everyNWeeks", { n: 4 })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Price */}
              <div>
                <Label className="text-xs">{t("recurringBlock.pricePerOccurrence")}</Label>
                <Input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="h-9 text-sm" />
              </div>

              {/* Revenue fields */}
              {isRevenue && (
                <>
                  <div>
                    <Label className="text-xs">{t("recurringBlock.estimatedValue")}</Label>
                    <Input value={editEstimatedValue} onChange={(e) => setEditEstimatedValue(e.target.value)} className="h-9 text-sm" placeholder="0" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-xs">{t("recurringBlock.confirmedRevenue")}</Label>
                      <p className="text-[10px] text-muted-foreground">{t("recurringBlock.confirmedRevenueHint")}</p>
                    </div>
                    <Switch checked={editConfirmedRevenue} onCheckedChange={setEditConfirmedRevenue} />
                  </div>
                </>
              )}

              {/* Comment */}
              <div>
                <Label className="text-xs">{t("recurringBlock.comment")}</Label>
                <Input value={editComment} onChange={(e) => setEditComment(e.target.value)} placeholder={t("recurringBlock.optionalComment")} className="h-9 text-sm" />
              </div>

              {/* Color */}
              <div>
                <Label className="text-xs">{t("recurringBlock.color")}</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer border" />
                  <span className="text-xs text-muted-foreground">{editColor}</span>
                </div>
              </div>

              {/* Save/Cancel */}
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  <Save className="h-3.5 w-3.5 mr-1" />
                  {saving ? t("recurringBlock.saving") : t("recurringBlock.save")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  Avbryt
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              {/* Details */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground text-xs">{t("recurringBlock.time")}</span>
                  <p className="font-medium">{startTime}–{endTime}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("recurringBlock.price")}</span>
                  <p className="font-medium">{block.price ? `${block.price} kr` : t("subscription.free")}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("recurringBlock.courts")}</span>
                  <p className="font-medium">{courtNames}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("recurringBlock.type")}</span>
                  <p className="font-medium">
                    <Badge variant="outline" className="text-[10px]">
                      {block.block_category === "operational" ? t("recurringBlock.operational") : t("recurringBlock.revenue")}
                    </Badge>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("recurringBlock.weekday")}</span>
                  <p className="font-medium">{DAY_NAMES[block.day_of_week ?? 0]}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("recurringBlock.interval")}</span>
                  <p className="font-medium">{(block.interval_weeks ?? 1) === 1 ? t("recurringBlock.everyWeek") : t("recurringBlock.everyNWeeks", { n: block.interval_weeks })}</p>
                </div>
                {block.valid_from && (
                  <div>
                    <span className="text-muted-foreground text-xs">{t("recurringBlock.validFrom")}</span>
                    <p className="font-medium">{block.valid_from}</p>
                  </div>
                )}
                {block.valid_until && (
                  <div>
                    <span className="text-muted-foreground text-xs">{t("recurringBlock.validUntil")}</span>
                    <p className="font-medium">{block.valid_until}</p>
                  </div>
                )}
                {block.estimated_value != null && block.estimated_value > 0 && (
                  <div>
                    <span className="text-muted-foreground text-xs">{t("recurringBlock.estimatedValue")}</span>
                    <p className="font-medium">{block.estimated_value} kr</p>
                  </div>
                )}
                {block.is_confirmed_revenue && (
                  <div>
                    <span className="text-muted-foreground text-xs">{t("recurringBlock.confirmedRevenue")}</span>
                    <p className="font-medium">
                      <Badge className="text-[10px] bg-green-600/15 text-green-700 border-green-300">{t("recurringBlock.yes")}</Badge>
                    </p>
                  </div>
                )}
              </div>
              {block.comment && (
                <div>
                  <span className="text-muted-foreground text-xs">{t("recurringBlock.comment")}</span>
                  <p className="text-sm">{block.comment}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button size="sm" variant="outline" onClick={() => { resetEditState(); setEditing(true); }}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Redigera
                </Button>
                <Button size="sm" variant="outline" onClick={handleSkipDate}>
                  <CalendarOff className="h-3.5 w-3.5 mr-1" />
                  {t("recurringBlock.skipDate", { date: format(selectedDate, "d MMM", { locale: sv }) })}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Ta bort
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("recurringBlock.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("recurringBlock.deleteDescription", { name: block.name || t("recurringBlock.title") })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("recurringBlock.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("recurringBlock.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
